import { Component, Element, Host, Prop, VNode, Watch, h } from '@stencil/core';
import { arrow, computePosition } from '@floating-ui/dom';
import { BasePlacement } from '@floating-ui/core/src/types';
import { guid, queryElementRoots } from '../../utils';

@Component({
  shadow: true,
  styleUrl: 'tooltip.css',
  tag: 'abds-tooltip',
})
/** @slot - Adds tooltip text. */
export class Tooltip {
  //--------------------------------------------------------------------------
  //
  //  Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsTooltipElement;

  //--------------------------------------------------------------------------
  //
  //  Props
  //
  //--------------------------------------------------------------------------

  /** ID selector to attach tooltip to. If unset Tooltip will try to attach to parent element. */
  @Prop({ reflect: true }) for: string;

  /** Determines Tooltip visibility. */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) open = false;

  /** Sets tooltip placement in relation to reference element. */
  @Prop({ reflect: true }) placement: BasePlacement = 'top';
  /**
   * TODO: expand placement to include additional options from floating-ui:
   *       i.e. import { Placement } from '@floating-ui/core/src/types';
   */

  //--------------------------------------------------------------------------
  //
  //  Prop Watchers
  //
  //--------------------------------------------------------------------------

  @Watch('for')
  protected forChanged(newValue: string, oldValue: string) {
    /** Remove event listeners on last attached element and add them to newly referenced element */
    this.assignEventListeners(newValue);
    this.assignEventListeners(oldValue, false);

    this.updateAriaDescribedBy(newValue);
    this.updateAriaDescribedBy(newValue, false);
  }

  //--------------------------------------------------------------------------
  //
  //  Private Properties
  //
  //--------------------------------------------------------------------------

  /** Reference to nub element */
  private nub: HTMLSpanElement;

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private assignEventListeners = (id: string, add = true) => {
    Object.entries({
      blur: this.hide,
      focus: this.show,
      mouseenter: this.show,
      mouseleave: this.hide,
    }).forEach(([event, listener]) => {
      (this.getAttachToElement(id) as any)[`${add ? 'add' : 'remove'}EventListener`](event, listener);
    });

    Object.entries({
      mouseenter: this.show,
      mouseleave: this.hide,
    }).forEach(([event, listener]) => {
      (this.host as any)[`${add ? 'add' : 'remove'}EventListener`](event, listener);
    });

    (document.body as any)[`${add ? 'add' : 'remove'}EventListener`]('keydown', this.onKeyDown as EventListener);
  };

  private attachNub = () => {
    this.nub = this.host.shadowRoot.querySelector('span.nub');

    if (!this.nub) {
      this.nub = document.createElement('span');
      this.nub.classList.add('nub');
      this.host.shadowRoot.querySelector('.inner-container').appendChild(this.nub);
    }
  };

  private getAttachToElement = (id: string): HTMLElement | null =>
    (id && queryElementRoots(this.host, `#${id}`)) || this.host.parentElement;

  private hide = () => {
    this.open = false;
  };

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') this.open = false;
  };

  private show = () => {
    this.updateTooltipPosition();
    this.open = true;
  };

  private updateAriaDescribedBy = (id: string, add = true) => {
    const attachToElement = this.getAttachToElement(id);
    const ariaDescribedBy = attachToElement.getAttribute('aria-describedBy');

    add
      ? !ariaDescribedBy?.includes(this.host.id) &&
        attachToElement.setAttribute('aria-describedby', `${ariaDescribedBy} ${this.host.id}`)
      : attachToElement.setAttribute('aria-describedBy', `${ariaDescribedBy.split(this.host.id)[0]}`);
  };

  private updateTooltipPosition = () => {
    const attachToElement = this.getAttachToElement(this.for);

    if (attachToElement) {
      this.attachNub();

      const computePositionOptions = {
        middleware: [arrow({ element: this.nub })],
        placement: this.placement,
      };

      computePosition(attachToElement, this.host, computePositionOptions).then(({ middlewareData, x, y }) => {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;
        const staticSide = {
          bottom: 'top',
          left: 'right',
          right: 'left',
          top: 'bottom',
        }[this.placement.split('-')[0]];

        Object.assign(this.nub.style, {
          bottom: '',
          left: arrowX !== undefined ? `${arrowX}px` : '',
          right: '',
          top: arrowY !== undefined ? `${arrowY}px` : '',
          [staticSide]: '-.25rem',
        });

        Object.assign(this.host.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    }
  };

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  componentDidRender() {
    if (this.open)
      // Using setTimeout to allow Tooltip to render visibly and hopefully final
      // layout painting of control element before trying to update its position
      setTimeout(() => {
        this.updateTooltipPosition();
      }, 500);
  }

  connectedCallback(): void {
    this.assignEventListeners(this.for);
    this.updateAriaDescribedBy(this.for);
  }

  disconnectedCallback(): void {
    this.assignEventListeners(this.for, false);
  }

  render(): VNode {
    return (
      <Host id={this.host.id || `abds-tooltip-${guid()}`} role="tooltip">
        <div class="inner-container">
          <slot />
        </div>
      </Host>
    );
  }
}
